import { isArray, isNil } from 'lodash-es';
import { formatTotalByLocale } from '@/utils/formatter';
import { isEnLang } from '@/utils/lang';
import { getStockClassWithDefaultGray } from '@/utils/oversea';
import i18n from '@/plugins/i18n';

/**
 * 个股 fullCode / 市场标识相关的通用业务工具。
 * @module utils/biz/stock
 */

/**
 * 个股 fullCode 结构匹配：`市场前缀 + 证券代码段`。
 * 市场前缀覆盖 A 股（sh/sz/bj）、港股（hk）、美股（us）、纳斯达克（nq）；
 * 证券代码段为剩余任意非空字符（A/港股数字码、美股字母码等）。
 * 仅用于已归一为小写紧凑形态（见 {@link normalizeStockFullCode}）的入参匹配。
 * @type {RegExp}
 */
const STOCK_FULLCODE_PATTERN = /^(sh|sz|bj|hk|us|nq)(.+)$/;

/**
 * 把任意常见形态的股票 fullCode 规范化为 canonical form（全小写、无分隔符）。
 * @param {unknown} code 原始 fullCode，支持 string / number 等可 String 化的入参
 * @returns {string} 规范化后的 fullCode；空值或无效入参返回空串
 *
 * @example
 * normalizeStockFullCode('SH.600519');   // 'sh600519'
 * normalizeStockFullCode('SH600519');    // 'sh600519'
 * normalizeStockFullCode('sh600519');    // 'sh600519'
 * normalizeStockFullCode('  SZ.000001 ');// 'sz000001'
 * normalizeStockFullCode(null);          // ''
 */
export function normalizeStockFullCode(code) {
  // 去掉市场与代码之间的点号（如 SH.600519 -> SH600519），再统一转小写得到 sh600519 形式
  const s = String(code ?? '')
    .trim()
    .replace(/\./g, '');
  return s ? s.toLowerCase() : '';
}

/**
 * 上行订阅 / 请求专用的 fullCode 规范化：去分隔符与首尾空白后，
 * **美股（`us` 前缀）保留证券代码段的原始大小写**，A 股 / 港股等其余市场沿用全小写。
 *
 * 背景：美股 fullCode 为字母代码且后端大小写敏感（如 `usNVDA` / `usMSFT`），
 * WebSocket 上行订阅（`stock_list_push` / `stock_header_detail`）必须原样回传，否则后端匹配不到标的；
 * A 股 / 港股为数字代码，大小写无意义，继续沿用 {@link normalizeStockFullCode} 的全小写口径，保持现状。
 *
 * 市场前缀统一归一为小写（`us`），仅保留证券代码段的原始大小写，避免因来源前缀大小写差异产生重复形态。
 *
 * @param {unknown} code 原始 fullCode，支持 string / number 等可 String 化的入参
 * @returns {string} 规范化后的 fullCode；空值或无效入参返回空串
 *
 * @example
 * normalizeStockFullCodeForSubscribe('usNVDA');    // 'usNVDA'
 * normalizeStockFullCodeForSubscribe('US.MSFT');   // 'usMSFT'
 * normalizeStockFullCodeForSubscribe('SH.600519'); // 'sh600519'
 * normalizeStockFullCodeForSubscribe('hk00700');   // 'hk00700'
 * normalizeStockFullCodeForSubscribe(null);        // ''
 */
export function normalizeStockFullCodeForSubscribe(code) {
  // 去掉市场与代码之间的点号并去除首尾空白，得到紧凑形态
  const compact = String(code ?? '')
    .trim()
    .replace(/\./g, '');
  if (!compact) {
    return '';
  }
  // 美股：字母代码且后端大小写敏感，市场前缀归一为小写、证券代码段保留原始大小写
  const usMatched = compact.match(/^us(.+)$/i);
  if (usMatched) {
    return `us${usMatched[1]}`;
  }
  // A 股 / 港股等：数字代码大小写无意义，沿用全小写规范化，保持现状
  return compact.toLowerCase();
}

/**
 * 在已知 key 集合中按 fullCode 反查与之等价的 key（先精确匹配，再走规范化反查）。
 *
 * 适用于不同来源 fullCode 大小写 / 分隔符不一致的对齐场景，例如：
 * - WebSocket 推送的 fullCode 与本地订阅 map 的 key 形态不一致
 * - 接口返回的 fullCode 与本地缓存 key 形态不一致
 * - 跨页面跳转传参时，需要把外部 fullCode 还原为本地约定的 key
 *
 * 查找顺序：
 * 1. 与 `knownKeys` 中的 key 精确相等
 * 2. 规范化后与某个 key 的规范化形式相等
 * 3. 都未命中则返回 `normalizeStockFullCode(rawCode)`，保证调用方拿到的始终是 canonical 形式
 *
 * @param {unknown} rawCode 待查找的原始 fullCode
 * @param {Iterable<string> | undefined | null} knownKeys 已知 key 集合（数组、Set、Map.keys() 等可迭代对象）
 * @returns {string} 命中的 key；未命中或入参为空时返回 canonical 形式
 *
 * @example
 * resolveStockFullCodeKey('sh600519', ['SH600519']);     // 'SH600519'
 * resolveStockFullCodeKey('SH.600519', ['SH600519']);    // 'SH600519'
 * resolveStockFullCodeKey('sh600519', new Set(['sh600519'])); // 'sh600519'
 * resolveStockFullCodeKey('sh600519', []);               // 'sh600519'（无命中，回退到 canonical）
 */
export function resolveStockFullCodeKey(rawCode, knownKeys) {
  const raw = String(rawCode ?? '').trim();
  if (!raw) {
    return '';
  }
  if (!knownKeys) {
    return normalizeStockFullCode(raw);
  }

  // 把 Iterable 物化为数组，便于先做一次精确匹配再做规范化反查
  const list = isArray(knownKeys) ? knownKeys : Array.from(knownKeys);

  // 精确命中：调用方用的就是同一形态的 key（含大小写 / 分隔符）
  if (list.includes(raw)) {
    return raw;
  }

  const norm = normalizeStockFullCode(raw);
  if (!norm) {
    return raw;
  }

  // 规范化反查：rawCode 与已知 key 的大小写 / 分隔符不同时仍可命中
  for (const key of list) {
    if (normalizeStockFullCode(key) === norm) {
      return key;
    }
  }

  return norm;
}

/**
 * 将 fullCode 格式化为 `编码.交易所`（如 sz000001 → 000001.SZ）。
 * @param {unknown} fullCode 原始 fullCode，支持 sz000001 / SH.600519 等常见形态
 * @returns {string} 格式化后的展示编码；无法解析时返回规范化 fullCode 或空串
 *
 * @example
 * formatStockCodeWithExchange('sz000001');   // '000001.SZ'
 * formatStockCodeWithExchange('SH.600519');  // '600519.SH'
 * formatStockCodeWithExchange('hk00700');    // '00700.HK'
 * formatStockCodeWithExchange('usAAPL');     // 'AAPL.US'
 */
export function formatStockCodeWithExchange(fullCode) {
  const normalized = normalizeStockFullCode(fullCode);
  if (!normalized) {
    return '';
  }
  // 匹配市场前缀 + 证券代码（A 股数字码 / 港股数字码 / 美股字母码等）
  const matched = normalized.match(STOCK_FULLCODE_PATTERN);
  if (!matched) {
    return normalized;
  }
  const [, market, code] = matched;
  return `${code.toUpperCase()}.${market.toUpperCase()}`;
}

/**
 * 把个股价格格式化为「保留两位小数」的标准展示文本。
 * 统一所有「最新价 / 现价 / 收盘价」等价格类字段的展示精度，
 * 缺省值约定：
 * - `null` / `undefined` / 字符串 `'--'` 视为缺省值，统一渲染 `'--'`
 * - 非数值入参（如 `Number(val)` 为 `NaN`）同样渲染 `'--'`
 * @param {unknown} val 原始价格值，支持 number / 数字字符串
 * @returns {string} 格式化后的展示文本
 *
 * @example
 * formatStockPrice(19);       // '19.00'
 * formatStockPrice('19.1');   // '19.10'
 * formatStockPrice(null);     // '--'
 * formatStockPrice('--');     // '--'
 * formatStockPrice('abc');    // '--'
 */
export function formatStockPrice(val) {
  // 与 changePct 缺省值保持一致：null / undefined / '--' / NaN 一律展示 '--'
  if (isNil(val) || val === '--') {
    return '--';
  }
  const num = Number(val);
  if (Number.isNaN(num)) {
    return '--';
  }
  return num.toFixed(2);
}

/**
 * 把个股涨跌幅原始值格式化为「+1.23% / -1.23% / 0% / --」形式
 * `handleTablePctText` 保持一致：
 * - `null` / `undefined` / 字符串 `'--'` 视为缺省值，统一渲染 `'--'`
 * - 数值为 0 渲染 `'0%'`，不展示 `+0.00%`
 * - 正数加 `+` 前缀，负数沿用 `Number().toFixed(2)` 自带的 `-` 前缀
 * 入参约定为「整百分比」单位（如 `1.23` 表示 1.23%），与服务端推送的 `changePct` 字段一致。
 * @param {unknown} val 原始涨跌幅
 * @returns {string} 格式化后的展示文本
 *
 * @example
 * formatStockChangePct(1.23);    // '+1.23%'
 * formatStockChangePct(-1.23);   // '-1.23%'
 * formatStockChangePct(0);       // '0%'
 * formatStockChangePct(null);    // '--'
 * formatStockChangePct('--');    // '--'
 */
export function formatStockChangePct(val) {
  if (isNil(val) || val === '--') {
    return '--';
  }
  const num = Number(val);
  if (Number.isNaN(num)) {
    return '--';
  }
  if (num === 0) {
    return '0%';
  }
  // 正数补 '+' 前缀；负数依赖 toFixed 自带 '-'，无需手动拼接
  return `${num > 0 ? '+' : ''}${num.toFixed(2)}%`;
}

/**
 * 按涨跌依据值差量同步元素的涨跌色 class。
 *
 * 内部以 `cacheKey` 在元素上记录"上一次写入的 class"，仅在 class 真正变化时才 `add/remove`，
 * 避免重复 DOM 操作；指令复用本工具时各自传入独立的 cacheKey 以免互相覆盖。
 *
 * 与「指令缓存键」的约定：
 * - `v-format` 推荐使用 `_vfColorClass`
 * - `v-stock-realtime` 推荐使用 `_vtColorClass`
 *
 * @param {HTMLElement} el 目标元素
 * @param {unknown} basis 涨跌依据值（如 changePct）；`null` / `undefined` 表示无可用值
 * @param {string} cacheKey 在元素上保存当前 class 的属性名
 * @param {{ fallbackToGray?: boolean }} [options]
 * @param {boolean} [options.fallbackToGray=false] basis 为 nil 时是否仍按 `getStockClassWithDefaultGray` 写入默认灰；
 *   默认 `false`（清除已有 class），适用于"无值就不上色"的静态展示；
 *   `true` 适用于"始终保留兜底灰"的实时行情展示。
 * @returns {void}
 */
export function syncStockColorClass(el, basis, cacheKey, { fallbackToGray = false } = {}) {
  // nil + 非兜底模式：返回 null 触发清除已有 class（避免静态文字被强行染灰）
  const next = isNil(basis) && !fallbackToGray ? null : getStockClassWithDefaultGray(basis);
  const prev = el[cacheKey] || null;
  if (prev === next) {
    return;
  }
  if (prev) {
    el.classList.remove(prev);
  }
  if (next) {
    el.classList.add(next);
  }
  el[cacheKey] = next;
}

/**
 * 清除元素上由 {@link syncStockColorClass} 写入的色 class（如指令 unmounted / 退订时调用）。
 *
 * @param {HTMLElement} el 目标元素
 * @param {string} cacheKey 与 syncStockColorClass 传入的 cacheKey 一致
 * @returns {void}
 */
export function clearStockColorClass(el, cacheKey) {
  if (el[cacheKey]) {
    el.classList.remove(el[cacheKey]);
    el[cacheKey] = null;
  }
}

/**
 * 将成交量数值格式化为带单位文案（股/手）的展示文本。
 *
 * - marketType = 3（美股）展示「股」
 * - 其他市场展示「手」
 * - 英文环境下单位前追加空格（如 `1.2M shares`）
 *
 * @param {unknown} value 原始成交量数值
 * @param {unknown} marketType 市场类型
 * @returns {string}
 */
export function formatVolumeWithUnit(value, marketType) {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) {
    return '0';
  }
  const base = formatTotalByLocale(num, 2);
  const unit =
    Number(marketType) === 3 ? i18n.global.t('selfSelected.detail.share') : i18n.global.t('selfSelected.detail.lot');
  return isEnLang() ? `${base} ${unit}` : `${base}${unit}`;
}
